import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,  
  CircularProgress, 
  Typography, 
  Tooltip, 
  IconButton, 
  FormControl, 
  MenuItem, 
  InputLabel, 
  Select,  
} from '@mui/material';
import { 
  Edit,
  Delete,
  DeleteForever,
  PersonAdd,
  Attribution,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import useSocket from 'hooks/useSocket';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import UserList from "./UserList";


const viewOptions = [
  { value: "users", label: "Users" },
  { value: "userLogs", label: "User Logs" },
  { value: "crewLogs", label: "Crew Logs" },
];

const Users = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "Users", "create", false);
  const canEdit = hasRight(permissionMap, "Users", "update", false);      
  const canDelete = hasRight(permissionMap, "Users", "delete", false);
  const canAuthorize = hasRight(permissionMap, "Users", "authorize", false);

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [users, setUsers] = useState([]);
  const [userLogs, setUserLogs] = useState([]);
  const [crewLogs, setCrewLogs] = useState([]);
  const [view, setView] = useState('users');

  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [userIdToDelete, setUserIdToDelete] = useState(null);

  const { onlineUsers } = useSocket(user._id); 
  const recCount = useMemo(() => {
    switch (view) {
      case 'users':
        return users?.length || 0;
      case 'userLogs':
        return userLogs?.length || 0;
      case 'crewLogs':
        return crewLogs?.length || 0;
      default:
        return users?.length || 0;
    }    
  }, [view, users, userLogs, crewLogs])


  const fetchUsers = useCallback(async () => {
    const res = await api.get('/users');
    return res?.data;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);
        
        const [
          usersResponse,
        ] = await Promise.all([
          fetchUsers(),
        ]);
        
        setUsers(usersResponse || []);
      } catch (error) {
        setError(error);
        toast.error(error.response?.data?.message || "Failed to fetch data!");
      } finally {
        setLoading(false);
      }
    };

    fetchData();      
}, [fetchUsers]); 

  const handleUserLogs = useCallback(async () => {
    if (!startDate || !endDate) return [];
    
    try {
      setError(null);
      setLoading(true);      

      const url = '/userlogs/userlogs';        
      const payload = { startDate, endDate }; 
      const res = await api.post(url, payload);

      setUserLogs(res.data || []);
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch user logs!");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate])

  const handleCrewLogs = useCallback(async () => {
    if (!startDate || !endDate) return [];
    
    try {
      setError(null);
      setLoading(true);      

      const url = '/userlogs/crewlogs';
      const payload = { startDate, endDate }; 
      const res = await api.post(url, payload);

      setCrewLogs(res.data || []);
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch crew logs!");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate])
  
  const handleViewChange = (e) => {
    const value = e.target.value;
    setView(value);

    switch (value) {
      case "userLogs":
        handleUserLogs();
        break;
      case "crewLogs":
        handleCrewLogs();
        break;
      default:
        break;
    }
  };

  
  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      ...isMobile ? { width: 60 } : { flex: 0.8 },
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    { 
      field: "firstName", 
      headerName: "FirstName", 
      ...isMobile ? { width: 150 } : { flex: 2 },
      valueFormatter: (value) => value?.toUpperCase() || "",
    },
    {
      field: "lastName",
      headerName: "LastName",
      ...isMobile ? { width: 120 } : { flex: 1.5 },
      valueFormatter: (value) => value?.toUpperCase() || "",
    },    
    {
      field: "email",
      headerName: "Email",
      ...isMobile ? { width: 250 } : { flex: 3 },
    },    
    {
      field: "role",
      headerName: "Role",
      ...isMobile ? { width: 100 } : { flex: 1.2 },
    },
    {
      field: "location",
      headerName: "Location",
      ...isMobile ? { width: 100 } : { flex: 1.1 },
    },
    {
      field: "occupation",
      headerName: "Occupation",
      ...isMobile ? { width: 150 } : { flex: 2 },
    },
    {
      field: "department",
      headerName: "Department",
      ...isMobile ? { width: 100 } : { flex: 1.3 },
      valueGetter: (params) => {
        const department = params[0];
        return department ? department.departmentCode : "N/A"; 
      },
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...isMobile ? { width: 100 } : { flex: 1.2 },
    },
    {
      field: "isChatVisible",
      headerName: "isChatVisible",
      ...isMobile ? { width: 100 } : { flex: 1.2 },
    },    
    {
      field: "loggedAt",
      headerName: "LoggedAt",
      ...isMobile ? { width: 150 } : { flex: 2.4 },
      renderCell: (params) => {
        const isOnline = onlineUsers?.includes(params.row._id);
        const dateStr = params.value
          ? format(new Date(params.value), "HH:mm:ss dd/MM/yyyy")
          : '';
    
        return (
          <FlexCenter height="100%" width="100%">
            <Typography
              variant="body2"
              sx={{ color: isOnline ? theme.palette.green.deep : "inherit" }}
            >
              {dateStr}
            </Typography>
          </FlexCenter>
        );
      },      
    },
    {
      field: "loggedOutAt",
      headerName: "LoggedOutAt",
      ...isMobile ? { width: 150 } : { flex: 2.4 },
      renderCell: (params) => {
        const dateStr = params.value
          ? format(new Date(params.value), "HH:mm:ss dd/MM/yyyy")
          : '';
    
        return (
          <FlexCenter height="100%" width="100%">
            <Typography
              variant="body2"
            >
              {dateStr}
            </Typography>
          </FlexCenter>
        );
      },      
    },         
  ], [theme, isMobile, onlineUsers]);

  const hiddenColumns = useMemo(() => {  
    return {
      _id: false,  
      no: false,   
      role: false,
      location: false,
      occupation: false,
      department: false,    
      isActive: false,  
      isChatVisible: false,  
      loggedOutAt: false,
    }
  }, []);

  const nonMobileHiddenColumns = useMemo(() => {
    return {
      _id: false, 
      isChatVisible: false,  
      loggedOutAt: false,
    }
  }, []);

  const actionColumn = useMemo(() => {        
    const activeCount = [canEdit, canAuthorize, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,      
        sortable: false,
        width: baseWidth,      
        renderCell: (params) => {        
          return (
            <FlexCenter
              gap={1}
              sx={{ width: '100%', height: '100%' }}
            >           
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{ color: "primary.dark" }}
                  onClick={() => navigate(`/users/${params.row._id}`)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>          
            
              <Tooltip title="Authorize">
                <IconButton
                  aria-label="Authorize"
                  sx={{ color: "primary.dark" }}
                  onClick={() => navigate(`/users/authorization/${params.row._id}`)}
                >
                  <Attribution />
                </IconButton>                
              </Tooltip>
            
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{ color: "primary.dark" }}
                  onClick={() => handleDeleteClick(params.row._id)}
                >
                  <DeleteForever />
                </IconButton>                
              </Tooltip>                    
            </FlexCenter>
          );
        },
      },
    ]
  }, [
    navigate,
    canEdit, 
    canAuthorize,
    canDelete,
  ]);

  const logColumns = [
    { field: "email", headerName: "Email", flex: 2 },
    { field: "action", headerName: "Action", flex: 1.5 },
    { field: "method", headerName: "Method", flex: 1 },
    { field: "endpoint", headerName: "Endpoint", flex: 2 },
    { field: "ip", headerName: "IP", flex: 1 },
    { field: "statusCode", headerName: "Status", flex: 1 },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 2,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), "HH:mm:ss dd/MM/yyyy") : "",
    },
  ];

  const handleDeleteClick = (userId) => {
    setUserIdToDelete(userId);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, users);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }

    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (deleteType === 'single') {
        if (!userIdToDelete) {
          toast.error('No user selected for deletion.');
          return;
        }
        
        await api.delete(`/users/delete/${userIdToDelete}`);

        setUsers(prev => prev.filter(item => item._id !== userIdToDelete));
        toast.success('User deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, users);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
                
        const results = await Promise.allSettled(
          selectedIds.map(id =>
            api.put(`/users/soft-delete/${id}`, 
              { }
            )
            .then(() => ({ status: 'fulfilled', id }))
            .catch(err => ({ status: 'rejected', id, error: err }))
          )
        );

        const failedDeletions = results.filter(result => result.status === 'rejected');
        const successfulDeletions = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value.id);

        if (failedDeletions.length > 0) {
          toast.error(`Failed to delete ${failedDeletions.length} user(s).`);
        }
        if (successfulDeletions.length > 0) {
          setUsers(users?.filter((item) => !rowSelectionModel.ids.has(item._id)));
          toast.success(`${successfulDeletions.length} user(s) deleted successfully.`);
        }
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete users!");
    }
  }, [users, deleteType, userIdToDelete, rowSelectionModel]);
 
  
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);
  
  if (loading) return (
    <FlexCenter height={LAYOUT_HEIGHT}>
      <CircularProgress />
    </FlexCenter>
  );
  
  if (error) return (
    <FlexCenter height={LAYOUT_HEIGHT}>
      <Typography color="error">{error?.message}</Typography>
    </FlexCenter>
  );

  
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Users" 
          subtitle={loading ? 'Loading...' : `List of ${recCount} Users`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && (
              <FormControl
                size="small"
                sx={{ width: { xs: '7rem', sm: '8rem' } }}
              >
                <InputLabel id="view-label">View</InputLabel>
                <Select
                  labelId="view-label"
                  label="View"
                  value={view}
                  onChange={handleViewChange}
                >
                  {viewOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
           
            <Box display="flex" alignItems="center" gap={1}>  
              {false && canDelete && (  
                <Tooltip title="Bulk soft delete" arrow>
                  <span>                    
                    <StyledButton 
                      disabled={!isAnyRowSelected(rowSelectionModel, users)}
                      onClick={() => {
                        if (!canDelete) return;
                        handleBulkDelete();
                      }}
                    >
                      <Delete />
                    </StyledButton>
                  </span>
                </Tooltip>
              )}

              {canCreate && (
                <Tooltip title="Create" arrow>
                  <span>
                    <StyledButton 
                      onClick={() => {
                        if (!canCreate) return;
                        navigate("/users/new");
                      }}
                    >
                      <PersonAdd />
                    </StyledButton>
                  </span>
                </Tooltip>
              )}
            </Box>           
          </Box>
        )}
      </FlexBetween>
      
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        gap={2}
      >  
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}  
          sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
        >                            
          {isMobile ? (
            <UserList
              rows={users}
              onDelete={handleDeleteClick}
              onEdit={(member) => navigate(`/users/${member._id}`)}
              onAuthorize={(member) => navigate(`/users/authorization/${member._id}`)}
            />
          ) : (
            <DataGridComponent
              rows={view === "users" ? users : view === "userLogs" ? userLogs : crewLogs}
              columns={view === "users" ? columns.concat(actionColumn) : logColumns}
              loading={loading}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}                
              props={{
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
              }}
              sx={{
                "& .MuiDataGrid-row.isActive-false": {
                  backgroundColor: `${theme.palette.primary.light} !important`,
                },
              }}
            />
          )}

          <ScrollToTop scrollRef={scrollRef} />
        </Box>                              
        
        <DeleteConfirm
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={executeDelete}
          deleteType={deleteType}
        />
      </Box>
    </Box>
  );
};

export default Users;
